import { Button, Checkbox } from '@restomarker/ui-kit';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactElement, type ReactNode } from 'react';

import { t } from '@/shared/i18n';

import {
  alertSettingsKey,
  saveErrorText,
  useDisconnectTelegram,
  useDisconnectTelegramChat,
  useIssueTelegramLink,
  useTelegramConnection,
  useUpdateTelegramChat,
  type TelegramChat,
  type TelegramLink,
} from './api';
import { QrCode } from './QrCode';
import styles from './notificationsDialog.module.css';

export interface TelegramSectionProps {
  /** Заголовок раздела — только когда рядом есть и раздел push. */
  showTitle: boolean;
  /** Без сети и в саппорт-сессии: подключать и отключать нельзя. */
  disabled: boolean;
  /**
   * Что приходит в Telegram, общее для учётки. `perChat` — сервер знает чаты (T-058):
   * вкл/выкл у каждого чата, отсюда — только пороги. Иначе — галочка канала, как раньше.
   */
  alerts: (perChat: boolean) => ReactNode;
  /**
   * Привязка по alert-settings (новый режим). Привязку замечает опрос `/me/telegram/`,
   * и настройки канала после неё перечитываются: иначе галочка и сетка ждали бы
   * следующего открытия окна.
   */
  settingsConnected?: boolean;
  /**
   * Отказ сохранения канала — в конце раздела, а не под галочкой: 409 (Telegram
   * отключили с другого устройства) возвращает раздел к «Подключить», и объяснение
   * сервера не должно пропасть вместе с галочкой.
   */
  saveError?: string | null;
}

/**
 * Telegram: чаты учётки и подключение ещё одного.
 *
 * Чатов несколько (T-058): у общей учётки точки у каждого сотрудника свой Telegram. Список
 * видит и правит любой, кто вошёл, — снятую галочку или отключение бот сообщает самому
 * чату, поэтому окно здесь ничего не переспрашивает.
 *
 * Ссылка выдаётся ПО НАЖАТИЮ, а не при открытии окна: окно, открытое «посмотреть», не
 * должно плодить ссылки. Ссылка — и кнопкой, и QR-кодом: кнопка — для своего телефона,
 * QR — для общего планшета кухни, где повар привязывает СВОЙ Telegram камерой телефона.
 * Пока ссылка выдана, окно ждёт привязку ИМЕННО по ней (`link_used`): чаты у учётки уже
 * есть, и «подключено» ничего не говорит. Команда `/start …` целиком — запасной путь:
 * на десктопе Telegram иногда открывает чат, проглатывая параметр.
 *
 * Сервер старше T-058 (нет `chats`) — прежний вид: один чат и «Отключить».
 */
export function TelegramSection({
  showTitle,
  disabled,
  alerts,
  settingsConnected,
  saveError = null,
}: TelegramSectionProps): ReactElement {
  const [link, setLink] = useState<TelegramLink | null>(null);
  const connection = useTelegramConnection({
    waiting: link !== null,
    linkId: link?.link_id ?? null,
  });
  const issue = useIssueTelegramLink();
  const client = useQueryClient();

  const chats = connection.data?.chats;
  const connected = connection.data?.connected ?? settingsConnected ?? false;
  // Прежний сервер: ни `chats`, ни `link_used` — о привязке говорит только `connected`.
  // Только по ОТВЕТУ сервера: пока запрос по новой ссылке в пути, данных нет вовсе.
  const legacyServer = connection.data !== undefined && chats === undefined;
  // Привязались по выданной ссылке — QR больше не нужен. Выводится из ответа, а не
  // сбросом состояния: опрос сам останавливается на том же признаке (`useTelegramConnection`).
  const bound =
    link !== null && (connection.data?.link_used === true || (legacyServer && connected));
  const waitingLink = bound ? null : link;

  // Настройки канала перечитываются после привязки: новый чат мог сразу получить
  // предупреждения (они уже включены у учётки), и сетка порогов должна появиться.
  useEffect(() => {
    if (bound || (connected && settingsConnected === false)) {
      void client.invalidateQueries({ queryKey: alertSettingsKey });
    }
  }, [bound, connected, settingsConnected, client]);

  function connect(): void {
    issue.mutate(undefined, { onSuccess: (data) => setLink(data) });
  }

  let body: ReactElement;
  if (waitingLink !== null) {
    body = <LinkBlock link={waitingLink.link} onCancel={() => setLink(null)} />;
  } else if (chats === undefined) {
    body = connected ? (
      <LegacyConnected
        username={connection.data?.telegram_username ?? ''}
        alerts={alerts(false)}
        disabled={disabled}
      />
    ) : (
      <Button variant="primary" disabled={disabled || issue.isPending} onClick={connect}>
        {t('telegram.connect')}
      </Button>
    );
  } else {
    body = (
      <>
        {chats.length > 0 ? (
          <>
            <p className={styles.muted}>{t('telegram.chatsHint')}</p>
            <ul className={styles.chats} aria-label={t('telegram.chats')}>
              {chats.map((chat) => (
                <ChatRow
                  key={chat.id}
                  chat={chat}
                  digestAvailable={connection.data?.digest_available ?? false}
                  disabled={disabled}
                />
              ))}
            </ul>
            {alerts(true)}
          </>
        ) : null}
        <Button
          variant={chats.length > 0 ? 'secondary' : 'primary'}
          disabled={disabled || issue.isPending}
          onClick={connect}
        >
          {chats.length > 0 ? t('telegram.connectMore') : t('telegram.connect')}
        </Button>
      </>
    );
  }

  return (
    <section className={styles.body} aria-label={t('notifications.telegram')}>
      {showTitle ? <h3 className={styles.sectionTitle}>{t('notifications.telegram')}</h3> : null}
      {body}
      {saveError ? (
        <p className={styles.problem} role="alert">
          {saveError}
        </p>
      ) : null}
    </section>
  );
}

interface LinkBlockProps {
  link: string;
  onCancel: () => void;
}

/** Выданная ссылка: QR для чужого телефона, кнопка для своего, команда — запасной путь. */
function LinkBlock({ link, onCancel }: LinkBlockProps): ReactElement {
  return (
    <>
      <p className={styles.status}>{t('telegram.qrHint')}</p>
      <div className={styles.qr}>
        <QrCode value={link} label={t('telegram.qrLabel')} />
      </div>
      <p className={styles.status}>{t('telegram.linkHint')}</p>
      <a className={styles.link} href={link} target="_blank" rel="noreferrer">
        {t('telegram.openBot')}
      </a>
      <p className={styles.muted}>{t('telegram.fallback')}</p>
      <code className={styles.command}>/start {link.split('start=')[1]}</code>
      <Button variant="ghost" onClick={onCancel}>
        {t('telegram.cancelLink')}
      </Button>
    </>
  );
}

const SINCE = new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: '2-digit' });

/** Как назвать чат в списке: имя из профиля, @username, дата — чтобы узнать СВОЙ. */
function chatTitle(chat: TelegramChat): string {
  return chat.telegram_name || (chat.telegram_username ? `@${chat.telegram_username}` : '—');
}

interface ChatRowProps {
  chat: TelegramChat;
  digestAvailable: boolean;
  disabled: boolean;
}

/**
 * Чат учётки: свои галочки и «Отключить».
 *
 * Галочка встаёт, когда сервер подтвердил: пока запрос идёт, строка неактивна. Отказ —
 * под строкой, словами сервера.
 */
function ChatRow({ chat, digestAvailable, disabled }: ChatRowProps): ReactElement {
  const update = useUpdateTelegramChat();
  const disconnect = useDisconnectTelegramChat();
  const busy = disabled || update.isPending || disconnect.isPending;
  const title = chatTitle(chat);
  const failure = update.error ?? disconnect.error;
  const meta = [
    chat.telegram_name && chat.telegram_username ? `@${chat.telegram_username}` : null,
    t('telegram.chatSince').replace('{date}', SINCE.format(new Date(chat.created_at))),
  ].filter(Boolean);

  return (
    <li className={styles.chat}>
      <div className={styles.chatHead}>
        <span className={styles.chatName}>{title}</span>
        <span className={styles.chatMeta}>{meta.join(' · ')}</span>
      </div>
      <Checkbox
        checked={chat.alerts}
        disabled={busy}
        onCheckedChange={(next) => update.mutate({ id: chat.id, patch: { alerts: next } })}
        label={t('telegram.chatAlerts')}
      />
      {digestAvailable ? (
        <Checkbox
          checked={chat.digest}
          disabled={busy}
          onCheckedChange={(next) => update.mutate({ id: chat.id, patch: { digest: next } })}
          label={t('telegram.chatDigest')}
        />
      ) : null}
      <Button
        variant="secondary"
        size="sm"
        disabled={busy}
        onClick={() => disconnect.mutate(chat.id)}
        aria-label={t('telegram.disconnectChat').replace('{name}', title)}
      >
        {t('telegram.disconnect')}
      </Button>
      {failure ? (
        <p className={styles.problem} role="alert">
          {saveErrorText(failure, null)}
        </p>
      ) : null}
    </li>
  );
}

interface LegacyConnectedProps {
  username: string;
  alerts: ReactNode;
  disabled: boolean;
}

/** Сервер старше T-058: один чат, его галочка и «Отключить» всё сразу. */
function LegacyConnected({ username, alerts, disabled }: LegacyConnectedProps): ReactElement {
  const disconnect = useDisconnectTelegram();
  return (
    <>
      <p className={styles.status}>
        {t('telegram.connected').replace('{name}', username ? `@${username}` : '—')}
      </p>
      {alerts}
      <Button
        variant="secondary"
        disabled={disabled || disconnect.isPending}
        onClick={() => disconnect.mutate()}
      >
        {t('telegram.disconnect')}
      </Button>
    </>
  );
}
